import { useState, useCallback, useRef } from "react";
import { getAuthToken } from "../../../../util/auth";
import { getApiChara } from "../../../../util/getApi";
import { Table, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import classes from "./IPDMedicinePrescription.module.css";
import { FaEdit, FaTrash, FaSave, FaTimes, FaPlus, FaEraser } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const IPDMedicinePrescription = ({ onMedicinesChange, medicines = [], diagnosis, onDiagnosisChange }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    // Refs for add form
    const medicineInput = useRef();
    const dosageInput = useRef();
    const instrInput = useRef();
    const daysInput = useRef();
    const medTypeInput = useRef();

    // State
    const [searchResults, setSearchResults] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null);
    const [editRowData, setEditRowData] = useState(null);
    const [doses, setDoses] = useState({
        breakfast: null,
        lunch: null,
        dinner: null
    });

    const handleSearch = async (term) => {
        if (!term) {
            setSearchResults([]);
            return;
        }
        const token = getAuthToken();
        if (!token || token === "EXPIRED") {
            navigate("/");
            return;
        }
        try {
            const apiChar = getApiChara();
            const response = await fetch(`${apiChar}/medicine/getMedicinesByNameOrBarcodeOrDisease?name=${encodeURIComponent(term)}`, {
                headers: { Authorization: "Bearer " + token },
            });
            if (response.ok) {
                const data = await response.json();
                setSearchResults(data);
            }
        } catch (err) {
            console.error("Medicine search error:", err);
        }
    };

    const handleMedicineSelect = (e) => {
        const selectedName = e.target.value;
        if (!selectedName) return;

        const found = searchResults.find((m) => m.name && m.name.toUpperCase() === selectedName.toUpperCase());

        const allowedTypes = [
            "TAB", "CAP", "SYR", "SUSP", "SAC", "INJ", "DROP", "EDRP", "LIQ", "OINT", "CRE", "GEL", "LOT", "SPRAY", "SUPP", "ROTACAP", "POWDER"
        ];

        if (found && found.medType) {
            let medTypeUpper = found.medType.toUpperCase();

            // Map full names to abbreviations if necessary
            if (medTypeUpper === "TABLET") medTypeUpper = "TAB";
            if (medTypeUpper === "CAPSULE") medTypeUpper = "CAP";
            if (medTypeUpper === "SYRUP") medTypeUpper = "SYR";
            if (medTypeUpper === "SUSPENSION") medTypeUpper = "SUSP";
            if (medTypeUpper === "INJECTION") medTypeUpper = "INJ";

            if (allowedTypes.includes(medTypeUpper)) {
                medTypeInput.current.value = medTypeUpper;
                return;
            }
        }
    };

    const handleAddMedicine = () => {
        const medName = medicineInput.current.value.trim().toUpperCase();
        if (!medName) return;

        const newMed = {
            medicineName: medName,
            dosage: dosageInput.current.value,
            instruction: instrInput.current.value,
            days: daysInput.current.value,
            type: medTypeInput.current.value,
            breakFastDose: doses.breakfast,
            lunchDose: doses.lunch,
            dinnerDose: doses.dinner,
        };

        onMedicinesChange([...medicines, newMed]);
        resetForm();
    };

    const resetForm = () => {
        medicineInput.current.value = "";
        dosageInput.current.value = "";
        instrInput.current.value = "";
        daysInput.current.value = "";
        medTypeInput.current.value = "";
        setDoses({ breakfast: null, lunch: null, dinner: null });
    };

    const handleDelete = (index) => {
        const updated = medicines.filter((_, i) => i !== index);
        onMedicinesChange(updated);
    };

    const handleClearAll = () => {
        if (medicines.length === 0) return;
        if (window.confirm(t("Are you sure you want to clear all added medicines?"))) {
            onMedicinesChange([]);
        }
    };

    const handleEdit = (index) => {
        setEditingIndex(index);
        setEditRowData({ ...medicines[index] });
    };

    const handleSaveEdit = (index) => {
        const updated = medicines.map((med, i) => (i === index ? editRowData : med));
        onMedicinesChange(updated);
        setEditingIndex(null);
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
    };

    const renderToggle = (key, label) => (
        <div className={classes.ratioBox}>
            <label className={classes.label}>{t(label)}</label>
            <div className="btn-group w-100" role="group">
                <button
                    type="button"
                    className={`btn btn-sm ${doses[key] === 'BEFORE' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    style={doses[key] === 'BEFORE' ? { backgroundColor: "var(--primary-color)", borderColor: "var(--primary-color)" } : {}}
                    onClick={() => setDoses(prev => ({ ...prev, [key]: prev[key] === 'BEFORE' ? null : 'BEFORE' }))}
                >
                    {t("Before")}
                </button>
                <button
                    type="button"
                    className={`btn btn-sm ${doses[key] === 'AFTER' ? 'btn-primary' : 'btn-outline-secondary'}`}
                    style={doses[key] === 'AFTER' ? { backgroundColor: "var(--primary-color)", borderColor: "var(--primary-color)" } : {}}
                    onClick={() => setDoses(prev => ({ ...prev, [key]: prev[key] === 'AFTER' ? null : 'AFTER' }))}
                >
                    {t("After")}
                </button>
            </div>
        </div>
    );

    const inputStyle = { borderColor: "#b0b0b0" };

    return (
        <div className={classes.medicineSection}>
            <div className={classes.card}>
                <div className="row g-2 align-items-end">
                    <div className="col-md-2">
                        <label className={classes.label}>{t("Type")}</label>
                        <select ref={medTypeInput} className="form-select" style={inputStyle}>
                            <option value="">{t("Select")}</option>
                            <option value="TAB">{t("TAB")}</option>
                            <option value="CAP">{t("CAP")}</option>
                            <option value="SYR">{t("SYR")}</option>
                            <option value="INJ">{t("INJ")}</option>
                            <option value="DROP">{t("DROP")}</option>
                            <option value="EDRP">{t("EDRP")}</option>
                            <option value="LIQ">{t("LIQ")}</option>
                            <option value="OINT">{t("OINT")}</option>
                            <option value="GEL">{t("GEL")}</option>
                            <option value="LOT">{t("LOT")}</option>
                            <option value="POWDER">{t("POWDER")}</option>
                            <option value="SPRAY">{t("SPRAY")}</option>
                        </select>
                    </div>
                    <div className="col-md-3">
                        <label className={classes.label}>{t("Medicine")}</label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder={t("Search Medicine...")}
                            ref={medicineInput}
                            onChange={(e) => {
                                handleSearch(e.target.value);
                                handleMedicineSelect(e);
                            }}
                            list="ipd-meds-list"
                            style={inputStyle}
                        />
                        <datalist id="ipd-meds-list">
                            {searchResults.map((m, idx) => (
                                <option key={idx} value={m.name} />
                            ))}
                        </datalist>
                    </div>
                    <div className="col-md-1">
                        <label className={classes.label}>{t("Dosage")}</label>
                        <input type="text" ref={dosageInput} className="form-control" style={inputStyle} />
                    </div>

                    {/* Ratios as Toggles */}
                    <div className="col-md-2">
                        {renderToggle('breakfast', 'Breakfast')}
                    </div>
                    <div className="col-md-2">
                        {renderToggle('lunch', 'Lunch')}
                    </div>
                    <div className="col-md-2">
                        {renderToggle('dinner', 'Dinner')}
                    </div>
                </div>

                <div className="row g-2 align-items-end mt-2">
                    <div className="col-md-6">
                        <label className={classes.label}>{t("Instructions")}</label>
                        <input type="text" ref={instrInput} className="form-control" style={inputStyle} />
                    </div>
                    <div className="col-md-2">
                        <label className={classes.label}>{t("Days")}</label>
                        <input type="number" ref={daysInput} className="form-control" style={inputStyle} />
                    </div>
                    <div className="col-md-4 d-flex gap-2">
                        <button className="btn-secondary-premium flex-grow-1" onClick={resetForm}>
                            <FaEraser className="me-2" /> {t("Clear")}
                        </button>
                        <button className="btn-premium flex-grow-1" onClick={handleAddMedicine}>
                            <FaPlus className="me-2" /> {t("Add Medicine")}
                        </button>
                    </div>
                </div>
            </div>

            <div className="table-responsive mt-3">
                <Table striped bordered hover className={classes.medTable}>
                    <thead>
                        <tr className="bg-light">
                            <th>{t("Sr.")}</th>
                            <th>{t("Name")}</th>
                            <th>{t("Type")}</th>
                            <th>{t("Dosage")}</th>
                            <th>{t("Breakfast")}</th>
                            <th>{t("Lunch")}</th>
                            <th>{t("Dinner")}</th>
                            <th>{t("Days")}</th>
                            <th>{t("Instr")}</th>
                            <th className="text-center">
                                <Button
                                    className="btn-outline-danger-premium py-0 px-2"
                                    size="sm"
                                    onClick={handleClearAll}
                                >
                                    <FaTrash className="me-1" /> {t("Clear All")}
                                </Button>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {medicines.length === 0 ? (
                            <tr>
                                <td colSpan="11" className="text-center text-muted py-3">{t("No medicines prescribed")}</td>
                            </tr>
                        ) : (
                            medicines.map((med, idx) => (
                                <tr key={idx}>
                                    <td>{idx + 1}</td>
                                    {editingIndex === idx ? (
                                        <>
                                            <td><input type="text" className="form-control form-control-sm" value={editRowData.medicineName} onChange={e => setEditRowData({ ...editRowData, medicineName: e.target.value })} /></td>
                                            <td><input type="text" className="form-control form-control-sm" value={editRowData.type} onChange={e => setEditRowData({ ...editRowData, type: e.target.value })} /></td>
                                            <td><input type="text" className="form-control form-control-sm" value={editRowData.dosage} onChange={e => setEditRowData({ ...editRowData, dosage: e.target.value })} /></td>
                                            <td><select className="form-select form-select-sm" value={editRowData.breakFastDose || ""} onChange={e => setEditRowData({ ...editRowData, breakFastDose: e.target.value })}><option value="">-</option><option value="BEFORE">{t("B")}</option><option value="AFTER">{t("A")}</option></select></td>
                                            <td><select className="form-select form-select-sm" value={editRowData.lunchDose || ""} onChange={e => setEditRowData({ ...editRowData, lunchDose: e.target.value })}><option value="">-</option><option value="BEFORE">{t("B")}</option><option value="AFTER">{t("A")}</option></select></td>
                                            <td><select className="form-select form-select-sm" value={editRowData.dinnerDose || ""} onChange={e => setEditRowData({ ...editRowData, dinnerDose: e.target.value })}><option value="">-</option><option value="BEFORE">{t("B")}</option><option value="AFTER">{t("A")}</option></select></td>
                                            <td><input type="number" className="form-control form-control-sm" value={editRowData.days} onChange={e => setEditRowData({ ...editRowData, days: e.target.value })} /></td>
                                            <td><input type="text" className="form-control form-control-sm" value={editRowData.instruction} onChange={e => setEditRowData({ ...editRowData, instruction: e.target.value })} /></td>
                                            <td className="text-center">
                                                <div className="d-flex gap-2 justify-content-center">
                                                    <Button variant="link" className="text-teal p-0" onClick={() => handleSaveEdit(idx)}><FaSave /></Button>
                                                    <Button variant="link" className="text-danger p-0" onClick={handleCancelEdit}><FaTimes /></Button>
                                                </div>
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td>{med.medicineName}</td>
                                            <td>{t(med.type)}</td>
                                            <td>{med.dosage}</td>
                                            <td>{med.breakFastDose ? (med.breakFastDose === 'BEFORE' ? t('B') : t('A')) : "-"}</td>
                                            <td>{med.lunchDose ? (med.lunchDose === 'BEFORE' ? t('B') : t('A')) : "-"}</td>
                                            <td>{med.dinnerDose ? (med.dinnerDose === 'BEFORE' ? t('B') : t('A')) : "-"}</td>
                                            <td>{med.days}</td>
                                            <td>{med.instruction}</td>
                                            <td className="text-center">
                                                <div className="d-flex gap-2 justify-content-center">
                                                    <Button variant="link" className="text-teal p-0" onClick={() => handleEdit(idx)}><FaEdit /></Button>
                                                    <Button variant="link" className="text-danger p-0" onClick={() => handleDelete(idx)}><FaTrash /></Button>
                                                </div>
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
            </div>
        </div>
    );
};

export default IPDMedicinePrescription;
